/**
 * Shared chrome for the two signed-out screens.
 *
 * A quiet brand panel on one side, the form on the other. The panel is the
 * only place in the product where the ember is allowed to be atmospheric — and
 * it collapses away below `lg` so the form is never competing with decoration
 * on a phone.
 */

import type { ReactNode } from 'react';
import { Logo } from '@/components/ui/Logo';
import { LangToggle } from '@/components/layout/LangToggle';
import { ThemeToggle } from '@/components/layout/ThemeToggle';

interface AuthLayoutProps {
  /** Marketing column — headline, supporting copy, proof points. */
  aside: ReactNode;
  children: ReactNode;
}

export function AuthLayout({ aside, children }: AuthLayoutProps) {
  return (
    <div className="grid min-h-svh lg:grid-cols-[1fr_minmax(28rem,34rem)]">
      <aside className="relative hidden flex-col justify-between overflow-hidden border-e bg-muted/40 p-12 lg:flex">
        {/* Ember bloom + hairline grid. Decorative only. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(60rem_38rem_at_25%_25%,var(--brand),transparent_62%)] opacity-[0.14]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-40 [background-image:linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] [background-size:5rem_5rem] [mask-image:radial-gradient(ellipse_at_40%_50%,#000_25%,transparent_72%)]"
        />
        {aside}
      </aside>

      <section className="relative flex min-h-svh items-center justify-center px-6 py-16 sm:px-10">
        <div className="absolute end-6 top-6 flex items-center gap-2">
          <LangToggle />
          <ThemeToggle />
        </div>
        <div className="w-full max-w-sm">{children}</div>
      </section>
    </div>
  );
}

/** The wordmark, used at the top of the brand panel. */
export function AuthBrand() {
  return (
    <div className="relative">
      <Logo size={26} />
    </div>
  );
}
